import React, { useState, useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Stack,
  FormGroup,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  MenuItem,
} from '@mui/material';

// COMPONENTS
import CountrySelect from '../../common/inputs/CountrySelect';
import ProviderPicker from '../../common/ProviderPicker';
import LanguagePicker from '../../common/LanguagePicker';
import ProvidersFormattedMessages from '../../../messages/admin/ProvidersFormattedMessages';
import AuthorisedContactForm from '../AuthorisedContactForm';

// UTILS
import { validateName } from '../../../utils/validate';

// NETWORKING
import useAdminRequest from '../../../requests/AdminRequest';
import useProvidersRequest from '../../../requests/ProviderRequest';

const ProvidersAddForm = ({ backButtonClick, errorHandler, setPageTitle }) => {
  const [edits, setEdits] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [xeroAccounts, setXeroAccounts] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const { getProviderExtra } = useAdminRequest();
  const { createProvider } = useProvidersRequest();

  useEffect(() => {
    setPageTitle(ProvidersFormattedMessages.createProvider);
    const fetchData = async () => {
      try {
        const extras = await getProviderExtra(errorHandler);
        setStatuses(extras.provider_statuses);
        setXeroAccounts(extras.xero_accounts);

        setEdits((prevEdits) => ({
          ...prevEdits,
          xero_accounts_id: extras.xero_accounts.find(
            (x) => x.account_name === 'USA Sales'
          ).xero_accounts_id,
        }));
      } catch (e) {
        setError(e.message);
      }
    };

    fetchData();
  }, [errorHandler]);

  /*
    Handles changes in the form
  */
  const handleFormChange = (name, value) => {
    console.log('handleFormChange', name, value);
    setEdits((prevEdits) => ({
      ...prevEdits,
      [name]: value,
    }));
  };

  /*
    Handles form submission
  */
  const handleSubmit = async (event) => {
    event.preventDefault();

    setIsSaving(true);

    if (!validateName(edits.provider_name)) {
      setError('Provider Name is required');
      setIsSaving(false);
      return;
    }

    try {
      // Create Provider
      await createProvider(edits, errorHandler);
      backButtonClick(true);
    } catch (e) {
      setError(e.message);
      setIsSaving(false);
    }
  };

  /*
    Receives back the default select language_id
  */
  const handleDefaultLanguage = (languageId) => {
    setEdits((prevEdits) => ({
      ...prevEdits,
      language_id: languageId,
    }));
  };

  /*
    Handles setting defaults for Select elements (element 0 if exists)
  */
  const returnDefault = (label, id) => {
    setEdits((prevEdits) => ({
      ...prevEdits,
      [label]: id,
    }));
  };

  /*
    Generates the Select control for Provider Statuses
  */
  const generateStatusSelect = () => {
    let statusOptions = statuses;

    // If no data from API, set default list
    if (!statusOptions || statusOptions.length === 0) {
      statusOptions = [
        { meaning: 'Active', code: 'A' },
        { meaning: 'Cancelled', code: 'C' },
        { meaning: 'Suspended', code: 'S' },
        { meaning: 'Trial', code: 'T' },
      ];
    }

    if (!edits.provider_status) {
      setTimeout(() => {
        setEdits((prevEdits) => ({
          ...prevEdits,
          provider_status: statusOptions[0].code,
        }));
      }, 100);
    }

    return (
      <FormControl fullWidth>
        <TextField
          select
          size="small"
          name="provider_status"
          value={edits.provider_status || statusOptions[0].code}
          onChange={(e) => handleFormChange('provider_status', e.target.value)}
          variant="outlined"
        >
          {statusOptions.map((status, index) => (
            <MenuItem key={index} value={status.code}>
              {status.meaning}
            </MenuItem>
          ))}
        </TextField>
      </FormControl>
    );
  };

  /*
    Generates the Select control for Provider Xero Accounts
  */
  const generateXeroSelect = () => {
    return (
      <FormControl fullWidth>
        <TextField
          select
          name="xero_accounts_id"
          size="small"
          value={edits.xero_accounts_id || ''}
          onChange={(e) => handleFormChange('xero_accounts_id', e.target.value)}
          variant="outlined"
        >
          {xeroAccounts.map((xero, index) => (
            <MenuItem key={index} value={xero.xero_accounts_id}>
              {xero.account_name}
            </MenuItem>
          ))}
        </TextField>
      </FormControl>
    );
  };

  /*
    Generates the form submission button
  */
  const generateButton = () => {
    if (isSaving) return <Typography>Creating...</Typography>;
    return (
      <Box>
        <Button
          variant="contained"
          color="secondary"
          type="submit"
          disabled={isSaving}
        >
          {isSaving ? 'Creating...' : 'Create Provider'}
        </Button>
      </Box>
    );
  };

  /*
    Generate Error Text
  */
  const generateError = () => {
    if (!error) return null;

    return <Alert severity="error">{error}</Alert>;
  };

  /*
    Generates Main Body content
  */
  const generateBody = () => {
    return (
      <Box
        component="form"
        onSubmit={handleSubmit}
        onChange={(e) => handleFormChange(e.target.name, e.target.value)}
      >
        <Stack gap={4} sx={{ maxWidth: 800 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }} required>
                  Name:
                </InputLabel>
                <TextField
                  required
                  name="provider_name"
                  size="small"
                  onChange={(e) =>
                    handleFormChange('provider_name', e.target.value)
                  }
                  variant="outlined"
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }}>Copy From</InputLabel>
                <ProviderPicker
                  optionName="copy_dlvry_from"
                  providerId={edits.copy_dlvry_from}
                  baseOnly
                  fullWidth
                  onChange={(_, newValue) =>
                    handleFormChange('copy_dlvry_from', newValue?.id)
                  }
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }}>Status</InputLabel>
                {generateStatusSelect()}
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }}>Country</InputLabel>
                <CountrySelect
                  name="country_id"
                  fullWidth
                  selectDefault
                  value={edits.country_id}
                  onChange={(_, newValue) =>
                    handleFormChange('country_id', newValue)
                  }
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }}>Language</InputLabel>
                <LanguagePicker
                  optionsName="language_id"
                  name="language_id"
                  returnDefaultLanguageId={handleDefaultLanguage}
                  selectedLanguageId={edits.language_id}
                  onChange={(value) => {
                    setEdits((prev) => ({
                      ...prev,
                      language_id: value,
                    }));
                  }}
                  errorHandler={errorHandler}
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="active_creation"
                      value={edits.active_creation}
                      onChange={(e) =>
                        handleFormChange('active_creation', e.target.checked)
                      }
                    />
                  }
                  label="Active Creation"
                />
              </FormGroup>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }}>Xero Account</InputLabel>
                {generateXeroSelect()}
              </Stack>
            </Grid>
          </Grid>

          <AuthorisedContactForm />

          <Stack gap={2}>
            <Typography fontSize="small">
              <Typography component="span" sx={{ color: 'secondary.light' }}>
                *
              </Typography>{' '}
              Indicates a required field
            </Typography>

            {generateError()}

            {generateButton()}
          </Stack>
        </Stack>
      </Box>
    );
  };

  return <Box>{generateBody()}</Box>;
};

export default ProvidersAddForm;
